import React from "react";
import type { Options } from "@ui/Highcharts";
import { useQOI } from "../../../useQOI";
import { useReportingTooltipRender } from "../../../useReportingTooltipRender";
import t from "../../../../../../i18n";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";
interface OptionParamerters {
  reporting: DiagnosisReporting;
  series: Highcharts.SeriesOptionsType[];
}

export function useOptions(
  _chart: React.RefObject<Highcharts.Chart>,
  { reporting, series }: OptionParamerters,
) {
  const translations = useTranslations();
  const [qoi] = useQOI(reporting);

  const renderTooltip = useReportingTooltipRender();

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const {
        custom: { project, failure },
      } = this.point as Highcharts.Point & {
        custom: { project: Record<string, string>; failure: Record<string, string> };
      };

      const projectTooltipInfo = [
        {
          name: t("reporting.filters.project.project"),
          value: project["name"] ?? null,
        },
        {
          name: t("reporting.charts.reparability_impacts_matrix.failure"),
          value: failure["displayName"] ?? null,
        },
        {
          name: t("reporting.charts.reparability_impacts_matrix.reparability"),
          value: this.point.name,
        },
        {
          name: t("reporting.charts.reparability_impacts_matrix.impact"),
          value: this.point.y ?? null,
          unit: qoi?.unit ?? "",
        },
      ];

      const failureInfo = [
        {
          name: t("reporting.filters.failure.category"),
          value: failure["failureCategoryId"] ?? null,
        },
        {
          name: t("reporting.filters.failure.degradationType"),
          value: failure["degradationTypeId"] ?? null,
        },
        { name: t("reporting.filters.failure.system"), value: failure["systemId"] ?? null },
        {
          name: t("reporting.filters.failure.equipmentType"),
          value: failure["equipmentTypeId"] ?? null,
        },
      ];

      return renderTooltip({
        values: [projectTooltipInfo, failureInfo],
      });
    },
    [renderTooltip, qoi],
  );

  return React.useMemo(
    (): Options | null =>
      reporting
        ? {
            initial: {},
            chart: {
              ignoreHiddenSeries: true,
              zoomType: "xy",
              marginTop: 20,
            },
            tooltip: {
              shared: true,
              formatter: constantTooltipFormatter,
            },
            series,
            plotOptions: {
              bubble: {
                maxSize: 40,
                marker: {
                  fillOpacity: 0.5,
                  lineWidth: 2,
                },
              },
            },
            xAxis: {
              type: "category",
              categories: [
                t("reparability", "REPARABILITY_0", translations),
                t("reparability", "REPARABILITY_1", translations),
                t("reparability", "REPARABILITY_2", translations),
                t("reparability.none"),
              ],
            },
            yAxis: {
              id: "internalGap",
              minPadding: 0.08,
              title: {
                text: t("reporting.fleet_performance.chart.internal_losses", translations),
                textAlign: "center",
                useHTML: true,
              },
              labels: {
                unit: qoi?.unit ?? "",
                sign: false,
                useHTML: true,
              },
              max: 0,
            },
            navigator: {
              enabled: false,
            },
          }
        : null,
    [constantTooltipFormatter, reporting, qoi, series, translations],
  );
}
